// File: src/components/Maintenance/MaintenanceCostTable.jsx

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const INITIAL = {
  staffing: { actual: 0, approved: 0 },
  procurement: { actual: 0, approved: 0 },
  other: { actual: 0, approved: 0 },
  total: { actual: 0, approved: 0 },
};

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatCurrency = (num) =>
  `£${toNum(num, 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getVarianceTone = (diff) => {
  if (diff > 0) return "text-red-400";
  if (diff < 0) return "text-emerald-400";
  return "text-slate-300";
};

const formatVariance = (actual, approved) => {
  const diff = toNum(actual) - toNum(approved);

  if (diff > 0) {
    return `-${formatCurrency(Math.abs(diff))}`;
  }

  if (diff < 0) {
    return `+${formatCurrency(Math.abs(diff))}`;
  }

  return formatCurrency(0);
};

const normalizeBucket = (raw) => {
  const obj = isObj(raw) ? raw : {};

  return {
    actual: toNum(
      obj.actual ??
        obj.actual_cost ??
        obj.actual_spend ??
        obj.spend ??
        obj.total_actual ??
        0
    ),
    approved: toNum(
      obj.approved ??
        obj.approved_budget ??
        obj.budget ??
        obj.total_approved ??
        0
    ),
  };
};

const normalizeMaintenanceCosts = (raw) => {
  const root = isObj(raw) ? raw : {};
  const summary = isObj(root.summary) ? root.summary : {};
  const totals = isObj(root.totals) ? root.totals : {};

  const staffing = normalizeBucket(root.staffing ?? summary.staffing);
  const procurement = normalizeBucket(root.procurement ?? summary.procurement);
  const other = normalizeBucket(root.other ?? summary.other);

  const computedTotal = {
    actual: staffing.actual + procurement.actual + other.actual,
    approved: staffing.approved + procurement.approved + other.approved,
  };

  const totalSource = root.total ?? totals.total ?? totals ?? summary.total;

  const total = isObj(totalSource)
    ? {
        actual: toNum(
          totalSource.actual ??
            totalSource.actual_cost ??
            totalSource.actual_spend ??
            computedTotal.actual
        ),
        approved: toNum(
          totalSource.approved ??
            totalSource.approved_budget ??
            totalSource.budget ??
            computedTotal.approved
        ),
      }
    : computedTotal;

  return {
    staffing,
    procurement,
    other,
    total,
  };
};

const categories = [
  { key: "staffing", label: "Staffing" },
  { key: "procurement", label: "Procurement" },
  { key: "other", label: "Other" },
];

const MaintenanceCostTable = ({
  data = null,
  loading = false,
  error = "",
  source = null,
  refreshKey = 0,
}) => {
  const permissions = useUserPermissions();

  const canViewMaintenanceCosts = useMemo(() => {
    if (typeof permissions?.canViewMaintenanceCosts === "boolean") {
      return permissions.canViewMaintenanceCosts;
    }

    return Boolean(
      permissions?.canSeeFinance ||
        permissions?.isExecutive ||
        permissions?.isAdmin ||
        permissions?.isDeveloper
    );
  }, [permissions]);

  const [costData, setCostData] = useState(INITIAL);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState("");

  const hasParentData = useMemo(() => isObj(data), [data]);

  useEffect(() => {
    let alive = true;

    if (!canViewMaintenanceCosts) {
      setLocalLoading(false);
      return () => {
        alive = false;
      };
    }

    if (hasParentData) {
      if (!alive) return;

      setCostData(normalizeMaintenanceCosts(data));
      setLocalError("");
      setLocalLoading(false);

      return () => {
        alive = false;
      };
    }

    (async () => {
      try {
        setLocalLoading(true);
        setLocalError("");

        const res = await api.get("/solo-mdsh-api/finance/maintenance-costs");

        if (!alive) return;
        setCostData(normalizeMaintenanceCosts(res?.data));
      } catch (err) {
        console.error("Error fetching maintenance costs:", err);

        if (!alive) return;

        setCostData(INITIAL);
        setLocalError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load maintenance cost data."
        );
      } finally {
        if (alive) setLocalLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [canViewMaintenanceCosts, hasParentData, data, refreshKey]);

  const effectiveLoading = loading && !hasParentData ? true : localLoading;
  const effectiveError = error || localError;

  if (!canViewMaintenanceCosts) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
        <h3 className="text-lg font-bold text-rose-800">Restricted Access</h3>
        <p className="mt-1 text-sm text-rose-700">
          You are not authorized to view maintenance financials.
        </p>
      </div>
    );
  }

  if (effectiveLoading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="h-6 w-72 animate-pulse rounded bg-slate-200" />
        <div className="mt-5 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-12 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="text-2xl font-semibold text-slate-900">
            Maintenance Cost Insights
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Budget versus actual spend across maintenance-related cost categories.
          </p>
        </div>

        <div className="flex flex-col items-start gap-1 md:items-end">
          {source ? <p className="text-[11px] text-slate-500">Source: {source}</p> : null}
        </div>
      </div>

      {effectiveError ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800">
          {effectiveError}
        </div>
      ) : null}

      <div className="mt-5 overflow-auto rounded-2xl border border-slate-200">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="p-3 text-left font-semibold">Category</th>
              <th className="p-3 text-right font-semibold">Actual Spend</th>
              <th className="p-3 text-right font-semibold">Approved Budget</th>
              <th className="p-3 text-right font-semibold">Variance</th>
            </tr>
          </thead>

          <tbody>
            {categories.map(({ key, label }) => {
              const item = costData[key] || { actual: 0, approved: 0 };
              const diff = toNum(item.actual) - toNum(item.approved);

              return (
                <tr key={key} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-medium text-slate-800">{label}</td>
                  <td className="p-3 text-right text-slate-700">
                    {formatCurrency(item.actual)}
                  </td>
                  <td className="p-3 text-right text-slate-700">
                    {formatCurrency(item.approved)}
                  </td>
                  <td className={`p-3 text-right font-semibold ${getVarianceTone(diff)}`}>
                    {formatVariance(item.actual, item.approved)}
                  </td>
                </tr>
              );
            })}

            <tr className="border-t border-slate-200 bg-slate-50 font-bold">
              <td className="p-3 text-slate-900">Total</td>
              <td className="p-3 text-right text-slate-900">
                {formatCurrency(costData.total.actual)}
              </td>
              <td className="p-3 text-right text-slate-900">
                {formatCurrency(costData.total.approved)}
              </td>
              <td
                className={`p-3 text-right font-semibold ${getVarianceTone(
                  toNum(costData.total.actual) - toNum(costData.total.approved)
                )}`}
              >
                {formatVariance(costData.total.actual, costData.total.approved)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MaintenanceCostTable;